import clsx from "clsx";
import { ArrowDown, ArrowUp, ChevronsUpDown, MoreHorizontal } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Menu, type MenuItem } from "./overlay.js";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Makes the column sortable by this value. */
  sort?: (row: T) => string | number;
  align?: "right";
  /** The column that names the row; it becomes the card title on phones. */
  primary?: boolean;
  /** Not shown in the phone card (secondary details). */
  hideOnPhone?: boolean;
  className?: string;
}

interface SortState {
  key: string;
  dir: "asc" | "desc";
}

/**
 * The list view used for every resource: sortable columns, optional selection
 * with a bulk-action bar, a row menu, an inline progress bar while an
 * operation runs, skeleton rows while loading. Below the `md` breakpoint the
 * rows turn into cards so nothing scrolls sideways on a phone.
 */
export function DataTable<T>({
  label,
  rows,
  rowKey,
  columns,
  loading = false,
  empty,
  rowMenu,
  progress,
  muted,
  selectable = false,
  bulkActions,
  initialSort,
}: {
  label: string;
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  columns: Column<T>[];
  loading?: boolean;
  /** Shown instead of the table when there are no rows. */
  empty?: ReactNode;
  rowMenu?: (row: T) => MenuItem[][];
  /** 0–100 while an operation runs on the row, otherwise null. */
  progress?: (row: T) => number | null;
  /** Greys a row out (unavailable, being deleted). */
  muted?: (row: T) => boolean;
  selectable?: boolean;
  bulkActions?: (selected: T[], clear: () => void) => ReactNode;
  initialSort?: SortState;
}) {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState | undefined>(initialSort);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  const sorted = useMemo(() => {
    const list = [...(rows ?? [])];
    if (!sort) return list;
    const column = columns.find((c) => c.key === sort.key);
    if (!column?.sort) return list;
    const value = column.sort;
    const factor = sort.dir === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      const x = value(a);
      const y = value(b);
      return (
        (typeof x === "number" && typeof y === "number"
          ? x - y
          : String(x).localeCompare(String(y))) * factor
      );
    });
  }, [rows, columns, sort]);

  if (loading || rows === undefined) return <SkeletonRows label={label} />;
  if (rows.length === 0 && empty) return <>{empty}</>;

  const keys = sorted.map(rowKey);
  const selectedRows = sorted.filter((row) => selected.has(rowKey(row)));
  const allSelected = keys.length > 0 && keys.every((k) => selected.has(k));
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const clear = () => setSelected(new Set());
  const cycleSort = (key: string) =>
    setSort((prev) =>
      prev?.key !== key
        ? { key, dir: "asc" }
        : prev.dir === "asc"
          ? { key, dir: "desc" }
          : undefined,
    );
  const primary = columns.find((c) => c.primary) ?? columns[0];

  return (
    <div className="flex flex-col gap-2">
      {selectable && selectedRows.length > 0 && bulkActions && (
        <div className="sticky top-14 z-10 flex flex-wrap items-center gap-3 rounded-xl border border-accent bg-accent-soft px-4 py-2 text-sm">
          <span className="font-medium">{t("table.selected", { count: selectedRows.length })}</span>
          <div className="flex flex-wrap gap-2">{bulkActions(selectedRows, clear)}</div>
          <button type="button" onClick={clear} className="ml-auto text-muted hover:text-text">
            {t("table.clearSelection")}
          </button>
        </div>
      )}

      {/* Desktop and tablet: a real table. */}
      <div className="relative hidden overflow-x-auto rounded-xl border border-line bg-surface md:block">
        <table className="w-full text-left text-sm" aria-label={label}>
          <thead className="border-b border-line bg-surface-2 text-xs text-muted">
            <tr>
              {selectable && (
                <th scope="col" className="w-10 px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={() => (allSelected ? clear() : setSelected(new Set(keys)))}
                    aria-label={t("table.selectAll")}
                    className="accent-accent"
                  />
                </th>
              )}
              {columns.map((column) => {
                const active = sort?.key === column.key ? sort.dir : undefined;
                return (
                  <th
                    key={column.key}
                    scope="col"
                    aria-sort={
                      active === "asc" ? "ascending" : active === "desc" ? "descending" : undefined
                    }
                    className={clsx(
                      "whitespace-nowrap px-4 py-2.5 font-medium",
                      column.align === "right" && "text-right",
                      column.className,
                    )}
                  >
                    {column.sort ? (
                      <button
                        type="button"
                        onClick={() => cycleSort(column.key)}
                        className={clsx(
                          "inline-flex min-h-6 items-center gap-1 hover:text-text",
                          active && "text-text",
                        )}
                      >
                        {column.header}
                        {active === "asc" ? (
                          <ArrowUp className="size-3.5" aria-hidden />
                        ) : active === "desc" ? (
                          <ArrowDown className="size-3.5" aria-hidden />
                        ) : (
                          <ChevronsUpDown className="size-3.5 opacity-50" aria-hidden />
                        )}
                      </button>
                    ) : (
                      column.header
                    )}
                  </th>
                );
              })}
              {rowMenu && (
                <th scope="col" className="w-12 px-2">
                  <span className="sr-only">{t("table.actions")}</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {sorted.map((row) => {
              const key = rowKey(row);
              const pct = progress?.(row) ?? null;
              return (
                <tr
                  key={key}
                  className={clsx(
                    "relative hover:bg-surface-2/60",
                    muted?.(row) && "text-muted",
                    selected.has(key) && "bg-accent-soft/50",
                  )}
                >
                  {selectable && (
                    <td className="px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => toggle(key)}
                        aria-label={t("table.selectRow")}
                        className="accent-accent"
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={clsx(
                        "px-4 py-2.5",
                        column.align === "right" && "whitespace-nowrap text-right tabular-nums",
                        column.className,
                      )}
                    >
                      {column.cell(row)}
                    </td>
                  ))}
                  {rowMenu && (
                    <td className="px-2 py-1 text-right">
                      <RowMenu groups={rowMenu(row)} />
                    </td>
                  )}
                  {pct !== null && <ProgressLine value={pct} cell />}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Phones: one card per row. */}
      <ul className="flex flex-col gap-2 md:hidden" aria-label={label}>
        {sorted.map((row) => {
          const key = rowKey(row);
          const pct = progress?.(row) ?? null;
          return (
            <li
              key={key}
              className={clsx(
                "relative overflow-hidden rounded-xl border border-line bg-surface px-4 py-3 text-sm",
                muted?.(row) && "text-muted",
              )}
            >
              <div className="flex items-start gap-3">
                {selectable && (
                  <input
                    type="checkbox"
                    checked={selected.has(key)}
                    onChange={() => toggle(key)}
                    aria-label={t("table.selectRow")}
                    className="mt-1 accent-accent"
                  />
                )}
                <div className="min-w-0 flex-1">{primary?.cell(row)}</div>
                {rowMenu && <RowMenu groups={rowMenu(row)} />}
              </div>
              <dl className="mt-2 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
                {columns
                  .filter((c) => c !== primary && !c.hideOnPhone)
                  .map((column) => (
                    <div key={column.key} className="contents">
                      <dt className="text-xs text-muted">{column.header}</dt>
                      <dd className="min-w-0 break-words">{column.cell(row)}</dd>
                    </div>
                  ))}
              </dl>
              {pct !== null && <ProgressLine value={pct} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RowMenu({ groups }: { groups: MenuItem[][] }) {
  const { t } = useTranslation();
  return (
    <Menu
      groups={groups}
      trigger={
        <button
          type="button"
          className="rounded-lg p-1.5 text-muted hover:bg-surface-2 hover:text-text"
          aria-label={t("table.actions")}
        >
          <MoreHorizontal className="size-5" aria-hidden />
        </button>
      }
    />
  );
}

/** Thin bar along the bottom edge of a row or card; `cell` when it sits inside a table row. */
function ProgressLine({ value, cell = false }: { value: number; cell?: boolean }) {
  const Tag = cell ? "td" : "div";
  return (
    <Tag className="absolute inset-x-0 bottom-0 h-0.5 p-0" aria-hidden>
      <span
        className="block h-full bg-accent transition-[width]"
        style={{ width: `${Math.max(4, value)}%` }}
      />
    </Tag>
  );
}

function SkeletonRows({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t("common.loading")}
      className="flex flex-col divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface"
    >
      <span className="sr-only">{label}</span>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <span className="h-3 w-1/4 animate-pulse rounded bg-surface-2" />
          <span className="h-3 w-1/6 animate-pulse rounded bg-surface-2" />
          <span className="h-3 w-1/5 animate-pulse rounded bg-surface-2" />
        </div>
      ))}
    </div>
  );
}
